/**
 * Theme Context for Application-wide Theme Management
 * 
 * @fileoverview React Context for managing application theme (light/dark mode)
 * Provides theme state, theme switching functionality, and localStorage persistence
 * @author Development Team
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Theme Context for sharing theme state across components
 * @type {React.Context}
 */
const ThemeContext = createContext();

/**
 * Custom hook to access theme context
 * 
 * @description Provides access to theme state and theme management functions.
 * Must be used within a ThemeProvider component.
 * 
 * @returns {Object} Theme context object containing:
 *   - theme: Current theme ('light' or 'dark')
 *   - setAdminTheme: Function to change theme (admin only)
 *   - isDark: Boolean indicating if current theme is dark
 *   - isLight: Boolean indicating if current theme is light
 * 
 * @throws {Error} If used outside of ThemeProvider
 * 
 * @example
 * const { theme, setAdminTheme, isDark } = useTheme();
 * 
 * // Change theme
 * setAdminTheme('dark');
 * 
 * // Check theme
 * if (isDark) {
 *   // Apply dark mode styles
 * }
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme Provider Component
 * 
 * @description Provides theme context to all child components.
 * Manages theme state, localStorage persistence, and applies theme to document body.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {React.ReactElement} ThemeProvider component
 * 
 * @example
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 */
export const ThemeProvider = ({ children }) => {
  /**
   * Initialize theme state from localStorage or default to 'light'
   * @type {[string, function]}
   */
  const [theme, setTheme] = useState(() => {
    // Get theme from admin settings or default to 'light'
    const adminTheme = localStorage.getItem('admin-theme');
    return adminTheme || 'light';
  });

  /**
   * Apply theme to document body when theme changes
   * 
   * @description Updates the data-theme attribute on document.body
   * to reflect the current theme for CSS styling.
   */
  useEffect(() => {
    // Apply theme to body
    document.body.setAttribute('data-theme', theme);
    // Don't save to localStorage here - only admin can change it
  }, [theme]);

  /**
   * Function to set theme (only for admin use)
   * 
   * @description Changes the application theme and persists it to localStorage.
   * This function is intended for admin use only and affects all users.
   * 
   * @param {string} newTheme - Theme to set ('light' or 'dark')
   * @throws {Error} If newTheme is not 'light' or 'dark'
   * 
   * @example
   * setAdminTheme('dark'); // Switch to dark mode
   * setAdminTheme('light'); // Switch to light mode
   */
  const setAdminTheme = (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') {
      throw new Error('Theme must be either "light" or "dark"');
    }
    setTheme(newTheme);
    localStorage.setItem('admin-theme', newTheme);
  };

  /**
   * Theme context value object
   * @type {Object}
   */
  const value = {
    theme,
    setAdminTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
